import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  Clock,
  XCircle,
  AlertTriangle,
  ArrowRight,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import { useOrders, useOrderStats, useOrderTimeline, useOrderFailures } from '../hooks/useOrders';
import { useUrnResolver } from '../hooks/useUrnResolver';
import StatusBadge from '../components/common/StatusBadge';
import OrdersChart from '../components/dashboard/OrdersChart';
import FailuresPanel from '../components/dashboard/FailuresPanel';
import TimeRangeSelector, {
  computeRange,
  type TimeRangeKey,
  type TimeRange,
} from '../components/dashboard/TimeRangeSelector';

const STATUS_GROUPS = [
  { key: 'completed', label: 'Completed', statuses: ['completed'], icon: CheckCircle, color: 'text-emerald-600 dark:text-emerald-400', dot: 'bg-emerald-500' },
  { key: 'processing', label: 'Processing', statuses: ['processing', 'pending', 'queued', 'created'], icon: Clock, color: 'text-amber-600 dark:text-amber-400', dot: 'bg-amber-500' },
  { key: 'failed', label: 'Failed', statuses: ['failed', 'error'], icon: XCircle, color: 'text-red-600 dark:text-red-400', dot: 'bg-red-500' },
  { key: 'canceled', label: 'Canceled', statuses: ['canceled', 'canceling', 'cancel.submitted', 'declined', 'withdrawn'], icon: AlertTriangle, color: 'text-gray-500 dark:text-gray-400', dot: 'bg-gray-400' },
];

function previousPeriodRange(range: TimeRange): { from?: string; to?: string } {
  if (!range.from || !range.to) return {};
  const fromMs = new Date(range.from).getTime();
  const toMs = new Date(range.to).getTime();
  const duration = toMs - fromMs;
  return { from: new Date(fromMs - duration).toISOString(), to: range.from };
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { resolveProductUrn, resolveLenderUrn } = useUrnResolver();

  const [rangeKey, setRangeKey] = useState<TimeRangeKey>('7d');
  const [range, setRange] = useState<TimeRange>(() => computeRange('7d'));
  const handleRangeChange = useCallback((key: TimeRangeKey, r: TimeRange) => { setRangeKey(key); setRange(r); }, []);

  const statsParams = useMemo(() => (range.from && range.to ? { from: range.from, to: range.to } : undefined), [range]);
  const { data: stats } = useOrderStats(statsParams);

  const prevRange = useMemo(() => previousPeriodRange(range), [range]);
  const prevStatsParams = useMemo(() => (prevRange.from && prevRange.to ? { from: prevRange.from, to: prevRange.to } : undefined), [prevRange]);
  const { data: prevStats } = useOrderStats(prevStatsParams);

  const timelineParams = useMemo(() => {
    if (!range.from || !range.to) {
      const now = new Date();
      const ago = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      return { from: ago.toISOString(), to: now.toISOString(), bucketMinutes: range.bucketMinutes };
    }
    return { from: range.from, to: range.to, bucketMinutes: range.bucketMinutes };
  }, [range]);
  const { data: timelineData, isLoading: timelineLoading } = useOrderTimeline(timelineParams);

  const failuresParams = useMemo(() => {
    const now = new Date();
    const ago = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const rangeFrom = range.from ? new Date(range.from) : ago;
    const effectiveFrom = rangeFrom < ago ? rangeFrom : ago;
    return { from: effectiveFrom.toISOString(), to: (range.to || now.toISOString()), limit: 20 };
  }, [range]);
  const { data: failuresData, isLoading: failuresLoading } = useOrderFailures(failuresParams);

  const { data: recentOrders, isLoading: ordersLoading } = useOrders({ limit: 10, sortField: 'creationDate', sortOrder: 'desc' });

  const statusCards = useMemo(() => {
    const byStatus = stats?.byStatus || {};
    const prevByStatus = prevStats?.byStatus || {};
    return STATUS_GROUPS.map((group) => {
      const count = group.statuses.reduce((sum, s) => sum + (byStatus[s] || 0), 0);
      const prevCount = group.statuses.reduce((sum, s) => sum + (prevByStatus[s] || 0), 0);
      const delta = prevStats ? count - prevCount : null;
      return { ...group, count, delta };
    });
  }, [stats, prevStats]);

  return (
    <div className="space-y-4 lg:space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Dashboard</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400">{(stats?.total ?? 0).toLocaleString()} orders tracked</p>
        </div>
        <TimeRangeSelector value={rangeKey} onChange={handleRangeChange} />
      </div>

      {/* Status cards -- minimal */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {statusCards.map((group) => (
          <button
            key={group.key}
            onClick={() => navigate(`/orders?status=${group.statuses[0]}`)}
            className="card p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors w-full"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${group.dot}`} />
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{group.label}</span>
              </div>
              {group.delta !== null && group.delta !== 0 && (
                <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium ${group.delta > 0 ? group.color : 'text-gray-400'}`}>
                  {group.delta > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  {group.delta > 0 ? '+' : ''}{group.delta}
                </span>
              )}
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{group.count.toLocaleString()}</p>
          </button>
        ))}
      </div>

      {/* Chart */}
      <OrdersChart from={range.from} to={range.to} buckets={timelineData?.buckets ?? []} timelineLoading={timelineLoading} />

      {/* Failures */}
      <FailuresPanel failures={failuresData?.failures ?? []} isLoading={failuresLoading} />

      {/* Recent Orders */}
      <div className="card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-gray-800">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Recent Orders</h2>
          <button onClick={() => navigate('/orders')} className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1">
            View all <ArrowRight className="h-3 w-3" />
          </button>
        </div>
        {ordersLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent" />
          </div>
        ) : !recentOrders?.data?.length ? (
          <div className="py-8 text-center text-xs text-gray-400 dark:text-gray-500">No orders found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-800">
                  {['Order', 'Product', 'Status', 'Lender', 'Created'].map((h) => (
                    <th key={h} className="px-4 py-2 text-left text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-800/50">
                {recentOrders.data.map((order) => (
                  <tr key={order.urn} onClick={() => navigate(`/orders/${order.uuid || order.urn}`)} className="hover:bg-gray-50 dark:hover:bg-gray-800/30 cursor-pointer transition-colors">
                    <td className="px-4 py-2.5"><span className="font-mono text-blue-600 dark:text-blue-400 truncate max-w-[160px] block">{order.urn}</span></td>
                    <td className="px-4 py-2.5"><span className="text-gray-900 dark:text-gray-200 truncate max-w-[140px] block" title={order.product}>{resolveProductUrn(order.product)}</span></td>
                    <td className="px-4 py-2.5"><StatusBadge status={order.status} /></td>
                    <td className="px-4 py-2.5"><span className="text-gray-600 dark:text-gray-400 truncate max-w-[120px] block" title={order.originator}>{resolveLenderUrn(order.originator)}</span></td>
                    <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400">
                      {order.createdAt ? new Date(order.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
